import React, { ReactNode } from 'react';
import clsx from 'clsx';
import { ParticleBackground } from '../ParticleBackground';
import { RocketShip, PlanetOrange } from '../index';
import { useOnboarding } from '../../context/OnboardingContext';

interface OnboardingStepWrapperProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
  className?: string;
  backgroundChildren?: ReactNode;
  showBackButton?: boolean;
  headerPosition?: 'top' | 'center';
  progressStep?: number;
  onProgressBack?: () => void;
  showRocketShip?: boolean;
  showPlanetOrange?: boolean;
}

const OnboardingStepWrapper: React.FC<OnboardingStepWrapperProps> = ({
  children,
  title,
  subtitle,
  className,
  backgroundChildren,
  showBackButton = false,
  headerPosition = 'center',
  progressStep,
  onProgressBack,
  showRocketShip = false,
  showPlanetOrange = false,
}) => {
  const { goToPreviousStep } = useOnboarding();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;
  return (
    <div
      className={clsx(
        'min-h-screen w-full flex items-center justify-center relative overflow-hidden',
        className,
      )}
    >
      <ParticleBackground subtle />
      {showRocketShip && <RocketShip />}
      {showPlanetOrange && !isMobile && <PlanetOrange />}
      {backgroundChildren}
      {/* Back button */}
      {showBackButton && (
        <button
          onClick={progressStep && progressStep > 0 ? onProgressBack : goToPreviousStep}
          className="absolute top-6 left-6 z-20 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow-md transition-colors"
          aria-label="Go back"
        >
          <svg
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}
      <div
        className={clsx(
          'relative z-10 w-full max-w-6xl flex flex-col items-center px-4 py-8',
          headerPosition === 'top' ? 'min-h-screen' : 'justify-center',
        )}
      >
        {headerPosition === 'top' ? (
          <>
            {/* Header section at top */}
            <div className="w-full text-center pt-8 pb-12">
              <h1 className="font-heading text-h1 md:text-5xl font-extrabold text-white mb-4">
                {title}
              </h1>
              {subtitle && (
                <p className="text-h4 font-heading text-gray-300 font-normal">{subtitle}</p>
              )}
            </div>

            {/* Content section */}
            <div className="flex-1 w-full flex justify-center">{children}</div>
          </>
        ) : (
          <>
            {/* Centered header and content */}
            <h1 className="font-heading text-h1 md:text-5xl font-extrabold text-white mb-4 text-center">
              {title}
            </h1>
            {subtitle && (
              <p className="text-h4 font-heading text-gray-300 font-normal mb-16 text-center">
                {subtitle}
              </p>
            )}
            {children}
          </>
        )}
      </div>
    </div>
  );
};

export default OnboardingStepWrapper;
